import { RefObject, useLayoutEffect, useRef, useState } from 'react';

interface UseInViewOptions {
  /** Encolhe a base da viewport, para revelar um pouco antes da borda. */
  rootMargin?: string;
  threshold?: number;
  /** Revela uma vez e para de observar. Padrão: true. */
  once?: boolean;
}

/**
 * Revelação por scroll.
 *
 * FALHA PARA VISÍVEL, sempre. O estado inicial é `true`, e só passa a `false`
 * depois de confirmarmos que existe um IntersectionObserver para desfazê-lo.
 * Sem observer (WebView antigo), sem JS, ou com prefers-reduced-motion, o
 * conteúdo nasce visível e assim permanece — a revelação é enfeite, e enfeite
 * que falha não pode levar o catálogo junto.
 *
 * A troca para `false` acontece em useLayoutEffect, antes do primeiro paint,
 * para não piscar visível -> escondido -> visível.
 */
export default function useInView<T extends HTMLElement = HTMLDivElement>({
  rootMargin = '0px 0px -10% 0px',
  threshold = 0.08,
  once = true,
}: UseInViewOptions = {}): [RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(true);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;

    setInView(false);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setInView(false);
          }
        });
      },
      { rootMargin, threshold },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [rootMargin, threshold, once]);

  return [ref, inView];
}
